"use client";

import { FormEvent, useState } from "react";
import { usePathname } from "next/navigation";

// List of company emails for reference (not used in the component but can be utilized for contact forms or staff directories)
const companyEmails = [
  "afia.yamfashije@hexad.co.rw",
  "aimable.munyampeta@hexad.co.rw",
  "chrispine.musanabera@hexad.co.rw",
  "claude.ndizera@hexad.co.rw",
  "denis.usanase@hexad.co.rw",
  "eliezer.nsengi@hexad.co.rw",
  "evode.munyampeta@hexad.co.rw",
  "francois.dusabe@hexad.co.rw",
  "gatete.prosper@hexad.co.rw",
  "gregoire.nsengiyumva@hexad.co.rw",
  "immaculate.uwizeye@hexad.co.rw",
  "info@hexad.co.rw",
  "janviere.nyirabagirishya@hexad.co.rw",
  "jhappy.manyenzi@hexad.co.rw",
  "liliane.uzabumwana@hexad.co.rw",
  "patrick.nshimiyimana@hexad.co.rw",
  "peter.karasira@hexad.co.rw",
  "theodette.ninshuti@hexad.co.rw",
];

export default function Footer() {
  const pathname = usePathname();
  const [isStaffLoginOpen, setIsStaffLoginOpen] = useState(false);
  const [staffEmail, setStaffEmail] = useState("");
  const [staffLoginMessage, setStaffLoginMessage] = useState("");

  const handleOpenStaffLogin = () => {
    setStaffLoginMessage("");
    setStaffEmail("");
    setIsStaffLoginOpen(true);
  };

  const handleCloseStaffLogin = () => {
    setIsStaffLoginOpen(false);
    setStaffLoginMessage("");
    setStaffEmail("");
  };

  const handleStaffLoginSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const email = staffEmail.trim().toLowerCase();

    if (!email) {
      setStaffLoginMessage("Please enter your company email.");
      return;
    }

    const isCompanyEmail = companyEmails.some(
      (companyEmail) => companyEmail.toLowerCase() === email,
    );

    if (!isCompanyEmail) {
      setStaffLoginMessage("The email can't be found.");
      return;
    }

    const url = `https://reporting-frontend-jaky.onrender.com/login?login_as=${encodeURIComponent(
      email,
    )}`;

    // Open the reporting login in a new tab and close the modal
    window.open(url, "_blank", "noopener,noreferrer");
    handleCloseStaffLogin();
  };

  return (
    <footer className="bg-[#0F2929] text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Company Info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex items-center space-x-3">
              <div className="flex flex-col">
                <span className="font-bold text-xl text-white">Hexad</span>
                <span className="text-sm text-[#0092B8]/70">
                  Humanity Counts
                </span>
              </div>
            </div>

            <p className="text-white/70 text-sm leading-relaxed max-w-md">
              Transforming industries through innovative technology solutions,
              engineering excellence, and strategic business transformation.
              Partner with us to drive sustainable growth and operational
              excellence.
            </p>

            <div className="flex space-x-3">
              <a
                href="#"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#0092B8] flex items-center justify-center transition-all duration-300 hover:-translate-y-0.5 group"
              >
                <svg
                  className="w-4 h-4 text-white/70 group-hover:text-white transition-colors duration-300"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
                </svg>
              </a>
              <a
                href="#"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#0092B8] flex items-center justify-center transition-all duration-300 hover:-translate-y-0.5 group"
              >
                <svg
                  className="w-4 h-4 text-white/70 group-hover:text-white transition-colors duration-300"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-white mb-4">
              Quick Links
            </h3>
            <ul className="space-y-3">
              {[
                { title: "Home", href: "/" },
                { title: "About Us", href: "/about" },
                { title: "Team", href: "/team" },
                { title: "Services", href: "/services" },
                { title: "Projects", href: "/projects" },
                // { title: "Why Choose Us", href: "/why-choose-us" },
                { title: "Contact", href: "/contact" },
              ].map((elem, index) => {
                return (
                  <li key={index}>
                    <a
                      href={elem.href}
                      className={`transition-colors duration-300 flex items-center group ${
                        pathname === elem.href
                          ? "text-[#0092B8]"
                          : "text-white/60"
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full bg-[#0092B8] mr-3 transition-all duration-300 ${
                          pathname === elem.href
                            ? "opacity-100"
                            : "opacity-0 group-hover:opacity-100"
                        }`}
                      ></span>
                      <span className="group-hover:text-white transition-colors duration-300">
                        {elem.title}
                      </span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Services */}
          {/* <div className="space-y-4">
            <h3 className="text-base font-semibold text-white mb-4">
              Our Services
            </h3>
            <ul className="space-y-3">
              {[
                "Supply Chain & Operational Services",
                "Tendering & Bidding Excellence",
                "Project Execution & Innovation",
                "Consultancy & Technical Support",
              ].map((name, index) => (
                <li key={index}>
                  <a
                    href="/services"
                    className="text-white/60 hover:text-white transition-colors duration-300 flex items-center group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0092B8] mr-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                    {name}
                  </a>
                </li>
              ))}
            </ul>
          </div> */}

          {/* Contact Info */}
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-white mb-4">
              Contact Us
            </h3>
            <div className="space-y-4 text-white/60 text-sm">
              <div className="flex items-start space-x-3 group">
                <svg
                  className="w-5 h-5 text-[#0092B8] mt-0.5 shrink-0 group-hover:text-[#0092B8] transition-colors duration-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span className="group-hover:text-white transition-colors duration-300">
                  KG 14 Ave, Kigali, Rwanda
                </span>
              </div>
              <div className="flex items-center space-x-3 group">
                <svg
                  className="w-5 h-5 text-[#0092B8] shrink-0 group-hover:text-[#0092B8] transition-colors duration-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                <span className="group-hover:text-white transition-colors duration-300">
                  +(250) 792 616 714
                </span>
              </div>
              <a
                href="mailto:info@hexad.co.rw"
                className="flex items-center space-x-3 group"
              >
                <svg
                  className="w-5 h-5 text-[#0092B8] shrink-0 group-hover:text-[#0092B8] transition-colors duration-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <span className="group-hover:text-white transition-colors duration-300">
                  info@hexad.co.rw
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-white/40 text-sm">
              © {new Date().getFullYear()} Hexad. All rights reserved.
            </div>
            <div className="flex space-x-6 text-sm">
              <button type="button" onClick={handleOpenStaffLogin}>
                <span className="text-white/40 hover:text-[#0092B8] transition-colors duration-300">
                  Staff Login
                </span>
              </button>
              <a
                href="/privacy-policy"
                className="text-white/40 hover:text-[#0092B8] transition-colors duration-300"
              >
                Privacy Policy
              </a>
              <a
                href="/terms-of-service"
                className="text-white/40 hover:text-[#0092B8] transition-colors duration-300"
              >
                Terms of Service
              </a>
              <a
                href="/cookie-preferences"
                className="text-white/40 hover:text-[#0092B8] transition-colors duration-300"
              >
                Preferences
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Staff Login Modal */}
      {isStaffLoginOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.15)] p-6">
            <div className="flex items-center justify-between mb-6">
              <h4 className="text-lg font-bold text-[#0F2929]">Staff Login</h4>
              <button
                type="button"
                onClick={handleCloseStaffLogin}
                className="text-gray-400 hover:text-[#0F2929] transition-colors duration-300 text-xl"
                aria-label="Close staff login popup"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleStaffLoginSubmit} className="space-y-5">
              <div>
                <label
                  htmlFor="staff-email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Company email
                </label>
                <input
                  id="staff-email"
                  type="email"
                  value={staffEmail}
                  onChange={(event) => {
                    setStaffEmail(event.target.value);
                    if (staffLoginMessage) {
                      setStaffLoginMessage("");
                    }
                  }}
                  placeholder="name@hexad.co.rw"
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0092B8]/50 focus:border-transparent transition-all duration-300"
                  autoFocus
                />
              </div>

              {staffLoginMessage && (
                <p className="text-sm text-amber-600">{staffLoginMessage}</p>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleCloseStaffLogin}
                  className="px-4 py-2 rounded-full border border-gray-300 text-gray-700 hover:border-[#0F2929] hover:text-[#0F2929] transition-all duration-300 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-full bg-[#0092B8] hover:bg-[#007a9a] text-white font-bold transition-all duration-300 hover:-translate-y-0.5 text-sm"
                >
                  Continue
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </footer>
  );
}
